/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */
import React from 'react';

import { Accordion, Button, Header, Icon, Modal } from '@mairistem/composants';

export interface Changement {
  titre?: string;
  description: string;
}

export interface SectionChangements {
  titre: string;
  changements: Changement[];
}

export interface NoteMiseAJour {
  titre: string;
  sections: SectionChangements[];
}

/**
 * Notes de mise à jour affichées dans la modal.
 * Ajouter un nouvel objet en HAUT du tableau pour déclencher
 * automatiquement l'affichage de la modal aux utilisateurs.
 */
export const NOTES_MISE_A_JOUR: NoteMiseAJour[] = [
  {
    titre: "Mise à jour d'avril 2026",
    sections: [
      {
        titre: 'Nouvelles fonctionnalités',
        changements: [
          {
            titre: 'Liaison Hubee',
            description:
              'Récupération et traitement des démarches effectuées en ligne sur service-public.fr avec la possibilité de transmettre au demandeur son attestation en ligne.',
          },
          {
            titre: 'Fiche recensé',
            description:
              "Accès direct aux documents associés à un individu.\n Ajout d'un historique sur la modification de rattachement d'un recensé aux listes de recensement (exemple : liste des non recensés → liste des recensés régularisés).",
          },
          {
            titre: 'Procédures de dénumérotation',
            description:
              "Déclenchement automatique d'une dénumération du trimestre si le nom, le prénom ou la date de recensement d'un individu est modifié.",
          },
          {
            titre: 'Tableau de bord',
            description:
              "Ajout d'un graphique reprenant les recensements, par type de liste et par année.",
          },
        ],
      },
      {
        titre: 'Corrections',
        changements: [
          { description: 'Génération du fichier PECOTO.' },
          {
            description:
              "Affichage des informations sur la notice individuelle et l'attestation.",
          },
          { description: "Optimisation de la clôture d'un trimestre." },
          {
            description:
              'Correspondance des adresses (prise en compte des apostrophes et des accents).',
          },
          { description: 'Champ date de la fiche du recensé.' },
          {
            description:
              "Numérotation de la liste des recensés inscrits dans l'ordre croissant des dates de naissance, noms puis prénoms.",
          },
        ],
      },
    ],
  },
];

interface NotesMiseAJourModalProps {
  open: boolean;
  onClose: () => void;
}

const NotesMiseAJourModal = ({ open, onClose }: NotesMiseAJourModalProps) => {
  const [activeIndex, setActiveIndex] = React.useState<number>(0);

  const handleClick = (_e: React.MouseEvent, titleProps: { index: number }) => {
    const { index } = titleProps;
    setActiveIndex(activeIndex === index ? -1 : index);
  };

  return (
    <Modal
      open={open}
      closeOnDimmerClick={false}
      closeOnDocumentClick={false}
      closeOnEscape
      closeIcon
      onClose={onClose}
      style={{ width: '700px' }}
    >
      <Modal.Header>Notes de mise à jour</Modal.Header>
      <Modal.Content>
        <Accordion fluid style={{ marginRight: '2em' }}>
          {NOTES_MISE_A_JOUR.map((note, index) => (
            <React.Fragment key={note.titre}>
              <Accordion.Title
                as={Header}
                active={activeIndex === index}
                index={index}
                onClick={handleClick}
              >
                <Icon name="dropdown" />
                <b>{note.titre}</b>
              </Accordion.Title>
              <Accordion.Content active={activeIndex === index}>
                {note.sections.map((section) => (
                  <React.Fragment key={section.titre}>
                    <Header
                      as="h5"
                      style={{ marginLeft: '2.5em', marginTop: '2em' }}
                    >
                      {section.titre}
                    </Header>
                    <ul style={{ marginLeft: '3em', marginTop: 0 }}>
                      {section.changements.map((changement) => (
                        <li
                          style={{ lineHeight: 1.6 }}
                          key={changement.description}
                        >
                          {changement.titre ? (
                            <>
                              <b>{`${changement.titre}`}</b>
                              <p
                                style={{ marginTop: '0.3em', marginBottom: 0 }}
                              >
                                <ul>
                                  {changement.description
                                    .split('\n')
                                    .map((line, i) => (
                                      // biome-ignore lint/suspicious/noArrayIndexKey: need
                                      <li key={`${line}-${i}`}>{` ${line}`}</li>
                                    ))}
                                </ul>
                              </p>
                            </>
                          ) : (
                            changement.description
                          )}
                        </li>
                      ))}
                    </ul>
                  </React.Fragment>
                ))}
              </Accordion.Content>
            </React.Fragment>
          ))}
        </Accordion>
      </Modal.Content>
      <Modal.Actions>
        <Button
          basic
          icon="close"
          content="Fermer"
          onClick={onClose}
          data-testid="modal-notes-maj-close"
        />
      </Modal.Actions>
    </Modal>
  );
};

export default NotesMiseAJourModal;
